import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { playerApi } from '../services/api';
import { Player } from '../models';

interface PlayerAutocompleteProps {
  value: Player | null;
  onChange: (value: Player | null) => void;
  error?: boolean;
  helperText?: string;
  matchId?: number | null;
  disabled?: boolean;
}

const PlayerAutocomplete: React.FC<PlayerAutocompleteProps> = ({
  value,
  onChange,
  error,
  helperText,
  matchId,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Handle input change with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch players when search term changes
  useEffect(() => {
    let active = true;

    const fetchPlayers = async () => {
      if (disabled || (searchTerm === '' && !matchId)) {
        return;
      }

      setLoading(true);
      try {
        const params: any = {
          search: searchTerm,
          page_size: 20,
        };
        
        // If match is selected, filter players by that match
        if (matchId) {
          params.match = matchId;
        }

        const response = await playerApi.getAll(params);
        if (active) {
          setOptions(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching players:', error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchPlayers();

    return () => {
      active = false;
    };
  }, [searchTerm, matchId, disabled]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disabled={disabled}
      size='small'
      renderOption={(props, option) => (
        <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
          {option.image && (
            <img
              loading="lazy"
              width="24"
              height="24"
              src={option.image}
              alt={option.name}
              style={{ borderRadius: '50%', marginRight: '8px' }}
            />
          )}
          <Box>
            <Typography variant="body2">{option.name}</Typography>
            {option.role && (
              <Typography variant="caption" color="text.secondary">
                {option.role}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Player"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default PlayerAutocomplete;
